import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface InfoDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InfoDialog({ open, onClose }: InfoDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('a[href], button')];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="info-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="info-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="panel-heading">
          <h2 id="info-title">{t('info.title')}</h2>
          <button
            ref={closeButtonRef}
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label={t('close')}
          >
            ×
          </button>
        </div>
        <p className="safety-copy">{t('info.safety')}</p>
        <p>{t('info.privacy')}</p>
        <p>{t('info.free')}</p>
        <p>{t('info.weatherLicense')}</p>
        <p>{t('info.calculation')}</p>
        <p>{t('info.obscurationModel')}</p>
        <h3>{t('info.sources')}</h3>
        <ul className="source-list">
          <li>
            <a href="https://openfreemap.org" target="_blank" rel="noreferrer">
              OpenFreeMap
            </a>
            ,{' '}
            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">
              © OpenStreetMap
            </a>{' '}
            and{' '}
            <a href="https://www.openmaptiles.org" target="_blank" rel="noreferrer">
              © OpenMapTiles
            </a>
          </li>
          <li>
            <a href="https://opendata.dwd.de" target="_blank" rel="noreferrer">
              DWD ICON-EU · CC BY 4.0
            </a>
          </li>
          <li>
            <a href="https://www.tomtom.com/legal" target="_blank" rel="noreferrer">
              © TomTom
            </a>
          </li>
          <li>
            <a href="https://registry.opendata.aws/terrain-tiles/" target="_blank" rel="noreferrer">
              Mapzen Terrain Tiles on AWS
            </a>
          </li>
          <li>
            <a
              href="https://eclipse.gsfc.nasa.gov/SEplot/SEplot2001/SE2026Aug12T.GIF"
              target="_blank"
              rel="noreferrer"
            >
              NASA/GSFC eclipse predictions
            </a>
          </li>
          <li>
            <a
              href="https://github.com/andrmoel/astronomy-bundle-js"
              target="_blank"
              rel="noreferrer"
            >
              Astronomy Bundle · MIT
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}
